class Core7HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.dots = [...this.querySelectorAll('[data-hero-dot]')];
    this.toggleButton = this.querySelector('[data-hero-toggle]');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Background videos: honor reduced motion regardless of slide count by
    // leaving every video paused on its poster frame.
    if (this.prefersReducedMotion) {
      this.slides.forEach((slide) => slide.querySelector('[data-hero-video]')?.pause());
    }

    if (this.slides.length <= 1) {
      return;
    }

    this.currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (this.currentIndex < 0) {
      this.currentIndex = 0;
    }

    this.autoplaySeconds = Number.parseInt(this.dataset.autoplaySeconds, 10);
    if (Number.isNaN(this.autoplaySeconds) || this.autoplaySeconds < 1) {
      this.autoplaySeconds = 10;
    }

    this.timer = null;
    this.isPaused = false;
    // Explicit, sticky pause from the toggle button — survives focus/blur and
    // dot clicks until the visitor presses play again.
    this.userPaused = false;

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = Number.parseInt(dot.dataset.heroDot, 10);
        if (!Number.isNaN(index)) {
          this.goTo(index);
        }
      });
    });

    this.toggleButton?.addEventListener('click', () => this.setUserPaused(!this.userPaused));

    // Swipe navigation for touch devices (the dots remain the visible control).
    this.touchStartX = null;
    this.touchStartY = null;
    this.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      },
      { passive: true }
    );
    this.addEventListener(
      'touchend',
      (event) => {
        if (this.touchStartX === null) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        this.touchStartX = null;
        // Only react to a deliberate, mostly-horizontal swipe so vertical page
        // scrolling still works. Swipe left = next, swipe right = previous.
        if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.goTo(this.currentIndex + (deltaX < 0 ? 1 : -1));
        }
      },
      { passive: true }
    );

    // Pause autoplay only while a control inside the hero is focused (keyboard
    // users). Hovering deliberately does NOT pause, so the slideshow keeps
    // advancing at the configured interval.
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.resume();
      }
    });

    this.setActiveSlide(this.currentIndex, false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  goTo(index) {
    const total = this.slides.length;
    const nextIndex = ((index % total) + total) % total;
    this.setActiveSlide(nextIndex, !this.prefersReducedMotion);
    this.startAutoplay();
  }

  setActiveSlide(index, animate = true) {
    this.currentIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (!animate) {
        slide.classList.add('is-instant');
        window.requestAnimationFrame(() => {
          slide.classList.remove('is-instant');
        });
      }

      // Play only the active slide's background video; keep the rest paused
      // (and all of them paused when the visitor prefers reduced motion).
      const video = slide.querySelector('[data-hero-video]');
      if (video) {
        if (isActive && !this.prefersReducedMotion && !this.userPaused) {
          // Restart from the beginning each time the slide becomes active so it
          // never resumes mid-clip; the autoplay interval controls how long it
          // shows before advancing.
          video.currentTime = 0;
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        } else {
          video.pause();
        }
      }
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  setUserPaused(paused) {
    this.userPaused = paused;

    if (this.toggleButton) {
      this.toggleButton.classList.toggle('is-playing', !paused);
      this.toggleButton.classList.toggle('is-paused', paused);
      this.toggleButton.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
    }

    const video = this.slides[this.currentIndex]?.querySelector('[data-hero-video]');
    if (paused) {
      this.stopAutoplay();
      video?.pause();
    } else {
      // Resume the current video from where it paused (don't restart — that's
      // reserved for natural slide changes).
      if (video && !this.prefersReducedMotion) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();

    if (this.prefersReducedMotion || this.isPaused || this.userPaused || this.slides.length <= 1) {
      return;
    }

    this.timer = window.setInterval(() => {
      this.goTo(this.currentIndex + 1);
    }, this.autoplaySeconds * 1000);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  pause() {
    this.isPaused = true;
    this.stopAutoplay();
  }

  resume() {
    this.isPaused = false;
    this.startAutoplay();
  }
}

if (!customElements.get('core7-hero-carousel')) {
  customElements.define('core7-hero-carousel', Core7HeroCarousel);
}
